import { wmsApi } from '../../../config/apiClient';
import { ACCOUNTING_API } from '../../../config/api';

export interface CargoSettings {
  id: string;
  warehouseId: string;
  provider: string;
  isDefault: boolean;
  isActive: boolean;
  apiUrl?: string;
  accountCode?: string;
  senderName?: string;
  senderPhone?: string;
  senderAddress?: string;
  senderCity?: string;
  senderDistrict?: string;
  settings?: string;
  hasApiKey: boolean;
  hasApiSecret: boolean;
}

export interface CreateCargoSettingsRequest {
  warehouseId: string;
  provider: string;
  isDefault: boolean;
  apiUrl?: string;
  apiKey?: string;
  apiSecret?: string;
  accountCode?: string;
  senderName?: string;
  senderPhone?: string;
  senderAddress?: string;
  senderCity?: string;
  senderDistrict?: string;
  settings?: string;
}

export interface UpdateCargoSettingsRequest {
  id: string;
  provider?: string;
  isDefault?: boolean;
  isActive?: boolean;
  apiUrl?: string;
  apiKey?: string;
  apiSecret?: string;
  accountCode?: string;
  senderName?: string;
  senderPhone?: string;
  senderAddress?: string;
  senderCity?: string;
  senderDistrict?: string;
  settings?: string;
}

export interface CreateShipmentResult {
  success: boolean;
  trackingNumber?: string;
  labelUrl?: string;
  message?: string;
  errorCode?: string;
}

export interface TrackingEvent {
  date: string;
  description: string;
  location?: string;
}

export interface TrackingResult {
  trackingNumber: string;
  status: string;
  lastUpdate: string;
  events: TrackingEvent[];
}

export const cargoService = {
  async getSettings(warehouseId: string): Promise<CargoSettings[]> {
    const resp = await wmsApi.get(ACCOUNTING_API.WMS_CARGO_SETTINGS(warehouseId));
    return resp.data?.data ?? [];
  },

  async createSettings(dto: CreateCargoSettingsRequest): Promise<{ id: string }> {
    const resp = await wmsApi.post(ACCOUNTING_API.WMS_CARGO_SETTINGS_CREATE, dto);
    return resp.data?.data;
  },

  async updateSettings(dto: UpdateCargoSettingsRequest): Promise<void> {
    await wmsApi.post(ACCOUNTING_API.WMS_CARGO_SETTINGS_UPDATE, dto);
  },

  async deleteSettings(id: string): Promise<void> {
    await wmsApi.post(ACCOUNTING_API.WMS_CARGO_SETTINGS_DELETE, { id });
  },

  async testConnection(id: string): Promise<{ success: boolean; message: string }> {
    const resp = await wmsApi.post(ACCOUNTING_API.WMS_CARGO_TEST, { id });
    return resp.data;
  },

  async getProviders(): Promise<string[]> {
    const resp = await wmsApi.get(ACCOUNTING_API.WMS_CARGO_PROVIDERS);
    return resp.data?.data ?? [];
  },

  async createShipment(orderId: string, cargoSettingsId?: string): Promise<CreateShipmentResult> {
    const resp = await wmsApi.post(ACCOUNTING_API.WMS_CARGO_CREATE_SHIPMENT, {
      orderId,
      cargoSettingsId: cargoSettingsId || undefined,
    });
    return resp.data?.data ?? resp.data;
  },

  async cancelShipment(orderId: string): Promise<{ success: boolean; message?: string }> {
    const resp = await wmsApi.post(ACCOUNTING_API.WMS_CARGO_CANCEL_SHIPMENT, { orderId });
    return resp.data?.data ?? resp.data;
  },

  async getLabel(orderId: string): Promise<Blob | null> {
    try {
      const resp = await wmsApi.get(ACCOUNTING_API.WMS_CARGO_LABEL(orderId), {
        responseType: 'blob',
      });
      return resp.data;
    } catch {
      return null;
    }
  },

  async getTracking(orderId: string): Promise<TrackingResult | null> {
    const resp = await wmsApi.get(ACCOUNTING_API.WMS_CARGO_TRACKING(orderId));
    return resp.data?.data ?? null;
  },

  async getTrackingBulk(warehouseId: string): Promise<any[]> {
    const resp = await wmsApi.get(ACCOUNTING_API.WMS_CARGO_TRACKING_BULK(warehouseId));
    return resp.data?.data ?? [];
  },
};
